import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useAttendancePaginated,
  useLeavePaginated,
  usePayroll,
  useApproveLeave,
  useUpdatePayroll,
  useDeletePayroll,
  useBulkDeletePayroll,
  useBulkUpdatePayrollStatus,
  useDownloadPayslip,
  usePrintPayslip,
  useDeleteAttendance,
  useBulkDeleteAttendance,
  useDeleteLeave,
  useBulkDeleteLeave,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Users,
  Loader2,
  AlertCircle,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  DollarSign,
  Search,
  X,
  Trash2,
  Eye,
  Banknote,
  Pencil,
  CalendarRange,
  Download,
  Printer,
  Landmark,
} from 'lucide-react';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { MonthPicker } from '../../components/ui/DatePicker';
import { employeeDisplayName, employeeInitials } from '../../utils/employee';
import { formatDateLabel } from '../../utils/dateFormat';
import type { HrEmployee } from '../../services/httpServices/hrService';
import { AttendanceModal } from './modals/AttendanceModal';
import { AttendanceEditModal } from './modals/AttendanceEditModal';
import { LeaveEditModal } from './modals/LeaveEditModal';
import { EmployeeDetailsModal } from './modals/EmployeeDetailsModal';
import { GeneratePayrollModal } from './modals/GeneratePayrollModal';
import { BankSalarySheetModal } from './modals/BankSalarySheetModal';

type Tab = 'attendance' | 'leave' | 'payroll';

const PAGE_LIMIT = 20;

const ATTENDANCE_FILTER_STATUSES = [
  { value: 'PRESENT', label: 'Present' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'LATE', label: 'Late' },
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'ON_LEAVE', label: 'On Leave' },
  { value: 'HOLIDAY', label: 'Holiday' },
];

const LEAVE_FILTER_STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function formatTime(value?: string) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

const currency = (value?: number | string) =>
  `৳${Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/** Renders the related employee (avatar + name + code/department). */
function EmployeeCell({
  employee,
  employeeId,
  onNameClick,
}: {
  employee?: HrEmployee | null;
  employeeId?: string;
  onNameClick?: () => void;
}) {
  if (!employee) {
    return (
      <span className="font-mono text-xs text-text-muted">
        {employeeId ? `${employeeId.slice(0, 8)}…` : 'Unknown employee'}
      </span>
    );
  }
  const name = employeeDisplayName(employee);
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold overflow-hidden">
        {employee.photoUrl ? (
          <img src={employee.photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          employeeInitials(employee)
        )}
      </div>
      <div className="min-w-0">
        {onNameClick ? (
          <button
            type="button"
            onClick={onNameClick}
            title="View employee details"
            className="block text-sm font-medium text-text hover:text-primary transition-colors text-left truncate"
          >
            {name}
          </button>
        ) : (
          <p className="text-sm font-medium text-text truncate">{name}</p>
        )}
        <p className="text-xs text-text-muted truncate">
          {[employee.employeeCode, employee.department?.name].filter(Boolean).join(' · ') || '—'}
        </p>
      </div>
    </div>
  );
}

const ATTENDANCE_STATUS: Record<string, { label: string; className: string }> = {
  PRESENT: { label: 'Present', className: 'bg-success/10 text-success' },
  ABSENT: { label: 'Absent', className: 'bg-danger/10 text-danger' },
  LATE: { label: 'Late', className: 'bg-warning/10 text-warning' },
  HALF_DAY: { label: 'Half Day', className: 'bg-warning/10 text-warning' },
  ON_LEAVE: { label: 'On Leave', className: 'bg-primary/10 text-primary' },
  HOLIDAY: { label: 'Holiday', className: 'bg-text-muted/10 text-text-muted' },
};

const LEAVE_STATUS: Record<string, { label: string; className: string; icon?: React.ElementType }> = {
  APPROVED: { label: 'Approved', className: 'bg-success/10 text-success', icon: CheckCircle },
  REJECTED: { label: 'Rejected', className: 'bg-danger/10 text-danger', icon: XCircle },
  PENDING: { label: 'Pending', className: 'bg-warning/10 text-warning', icon: Clock },
  CANCELLED: { label: 'Cancelled', className: 'bg-text-muted/10 text-text-muted' },
};

const PAYROLL_STATUS: Record<string, { label: string; className: string }> = {
  PAID: { label: 'Paid', className: 'bg-success/10 text-success' },
  FINALIZED: { label: 'Finalized', className: 'bg-primary/10 text-primary' },
  PROCESSED: { label: 'Processed', className: 'bg-primary/10 text-primary' },
  DRAFT: { label: 'Draft', className: 'bg-warning/10 text-warning' },
};

function StatusPill({
  map,
  value,
}: {
  map: Record<string, { label: string; className: string; icon?: React.ElementType }>;
  value: string;
}) {
  const meta = map[value] ?? { label: value, className: 'bg-text-muted/10 text-text-muted' };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${meta.className}`}>
      {Icon && <Icon size={12} />}
      {meta.label}
    </span>
  );
}

/** Shared filter card: search input + optional selects + clear button. */
function FilterBar({
  search,
  onSearch,
  placeholder,
  children,
  hasActiveFilters,
  onClear,
}: {
  search: string;
  onSearch: (value: string) => void;
  placeholder: string;
  children?: React.ReactNode;
  hasActiveFilters: boolean;
  onClear: () => void;
}) {
  return (
    <div className="bg-surface rounded-xl border border-border p-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        {children}
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/** Selection toolbar shared by the three record tables. */
function BulkBar({
  count,
  deleting,
  onDelete,
  onClear,
}: {
  count: number;
  deleting: boolean;
  onDelete: () => void;
  onClear: () => void;
}) {
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm text-text">
        <span className="font-medium">{count}</span> selected
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
        >
          {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          Delete
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={deleting}
          className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          <X size={14} />
          Clear selection
        </button>
      </div>
    </div>
  );
}

const selectClass =
  'px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50';

export function Hr() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const t = params.get('tab');
    return t === 'attendance' || t === 'leave' || t === 'payroll' ? t : 'attendance';
  });
  const [modal, setModal] = useState<Tab | null>(null);
  const [generatePayrollOpen, setGeneratePayrollOpen] = useState(false);
  const [bankSheetOpen, setBankSheetOpen] = useState(false);

  // Shared employee details modal.
  const [viewingEmployeeId, setViewingEmployeeId] = useState<string | null>(null);
  // Payroll record tied to the employee details modal (payroll tab only).
  const [viewingEmployeePayroll, setViewingEmployeePayroll] = useState<any | null>(null);

  // ── Attendance list state ────────────────────────────────────────────────
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendanceSearch, setAttendanceSearch] = useState('');
  const debouncedAttendanceSearch = useDebounce(attendanceSearch, 350);
  const [attendanceStatus, setAttendanceStatus] = useState('');
  const [selectedAttendanceIds, setSelectedAttendanceIds] = useState<string[]>([]);
  const [attendanceDeleteTarget, setAttendanceDeleteTarget] = useState<any | null>(null);
  const [attendanceBulkConfirmOpen, setAttendanceBulkConfirmOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<any | null>(null);

  const {
    data: attendanceData,
    isLoading: attendanceLoading,
    error: attendanceError,
  } = useAttendancePaginated({
    page: attendancePage,
    limit: PAGE_LIMIT,
    search: debouncedAttendanceSearch || undefined,
    status: attendanceStatus || undefined,
  });

  // ── Leave list state ─────────────────────────────────────────────────────
  const [leavePage, setLeavePage] = useState(1);
  const [leaveSearch, setLeaveSearch] = useState('');
  const debouncedLeaveSearch = useDebounce(leaveSearch, 350);
  const [leaveStatus, setLeaveStatus] = useState('');
  const [selectedLeaveIds, setSelectedLeaveIds] = useState<string[]>([]);
  const [leaveDeleteTarget, setLeaveDeleteTarget] = useState<any | null>(null);
  const [leaveBulkConfirmOpen, setLeaveBulkConfirmOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState<any | null>(null);

  const { data: leaveData, isLoading: leaveLoading } = useLeavePaginated({
    page: leavePage,
    limit: PAGE_LIMIT,
    search: debouncedLeaveSearch || undefined,
    status: leaveStatus || undefined,
  });

  // ── Payroll list state ───────────────────────────────────────────────────
  const [payrollPage, setPayrollPage] = useState(1);
  const [payrollSearch, setPayrollSearch] = useState('');
  const debouncedPayrollSearch = useDebounce(payrollSearch, 350);
  const [payrollStatus, setPayrollStatus] = useState('');
  const [payrollPeriod, setPayrollPeriod] = useState('');
  const [selectedPayrollIds, setSelectedPayrollIds] = useState<string[]>([]);
  const [payrollDeleteTarget, setPayrollDeleteTarget] = useState<any | null>(null);
  const [payrollBulkConfirmOpen, setPayrollBulkConfirmOpen] = useState(false);
  const [bulkPayrollStatus, setBulkPayrollStatus] = useState('');
  const [viewingPayroll, setViewingPayroll] = useState<any | null>(null);

  const { data: payrollData, isLoading: payrollLoading } = usePayroll({
    page: payrollPage,
    limit: PAGE_LIMIT,
    search: debouncedPayrollSearch || undefined,
    status: payrollStatus || undefined,
    payPeriod: payrollPeriod || undefined,
  });

  const approveLeave = useApproveLeave();
  const updatePayroll = useUpdatePayroll();
  const deletePayroll = useDeletePayroll();
  const bulkDeletePayroll = useBulkDeletePayroll();
  const bulkUpdatePayrollStatus = useBulkUpdatePayrollStatus();
  const downloadPayslip = useDownloadPayslip();
  const printPayslip = usePrintPayslip();
  const deleteAttendance = useDeleteAttendance();
  const bulkDeleteAttendance = useBulkDeleteAttendance();
  const deleteLeave = useDeleteLeave();
  const bulkDeleteLeave = useBulkDeleteLeave();

  const attendance = useMemo(() => attendanceData?.data || [], [attendanceData]);
  const leave = useMemo(() => leaveData?.data || [], [leaveData]);
  const payroll = useMemo(() => payrollData?.data || [], [payrollData]);
  const attendanceMeta = attendanceData?.meta;
  const leaveMeta = leaveData?.meta;
  const payrollMeta = payrollData?.meta;

  // Sync active tab with the URL (?tab=)
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (activeTab !== 'attendance') newParams.set('tab', activeTab);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [activeTab, navigate, location.pathname]);

  // Reset to the first page whenever a filter changes.
  useEffect(() => {
    setAttendancePage(1);
  }, [attendanceSearch, attendanceStatus]);
  useEffect(() => {
    setLeavePage(1);
  }, [leaveSearch, leaveStatus]);
  useEffect(() => {
    setPayrollPage(1);
  }, [payrollSearch, payrollStatus, payrollPeriod]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedAttendanceIds((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(attendance.map((r: any) => r.id));
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [attendance]);
  useEffect(() => {
    setSelectedLeaveIds((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(leave.map((r: any) => r.id));
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [leave]);
  useEffect(() => {
    setSelectedPayrollIds((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(payroll.map((r: any) => r.id));
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [payroll]);

  const employeeIdOf = (record: any): string | null =>
    record?.employee?.id || record?.employeeId || null;

  // ── Generic selection helpers ────────────────────────────────────────────
  const makeSelection = (
    rows: any[],
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
  ) => ({
    allSelected: rows.length > 0 && rows.every((r) => selected.includes(r.id)),
    someSelected: rows.some((r) => selected.includes(r.id)),
    toggleAll: () => setSelected(rows.every((r) => selected.includes(r.id)) ? [] : rows.map((r) => r.id)),
    toggleOne: (id: string) =>
      setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
  });

  const attendanceSelection = makeSelection(attendance, selectedAttendanceIds, setSelectedAttendanceIds);
  const leaveSelection = makeSelection(leave, selectedLeaveIds, setSelectedLeaveIds);
  const payrollSelection = makeSelection(payroll, selectedPayrollIds, setSelectedPayrollIds);

  // ── Attendance actions ───────────────────────────────────────────────────
  const handleDeleteAttendance = async () => {
    if (!attendanceDeleteTarget) return;
    try {
      await deleteAttendance.mutateAsync(attendanceDeleteTarget.id);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setAttendanceDeleteTarget(null);
    }
  };

  const handleBulkDeleteAttendance = async () => {
    try {
      await bulkDeleteAttendance.mutateAsync(selectedAttendanceIds);
      setSelectedAttendanceIds([]);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setAttendanceBulkConfirmOpen(false);
    }
  };

  // ── Leave actions ────────────────────────────────────────────────────────
  const handleDeleteLeave = async () => {
    if (!leaveDeleteTarget) return;
    try {
      await deleteLeave.mutateAsync(leaveDeleteTarget.id);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setLeaveDeleteTarget(null);
    }
  };

  const handleBulkDeleteLeave = async () => {
    try {
      await bulkDeleteLeave.mutateAsync(selectedLeaveIds);
      setSelectedLeaveIds([]);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setLeaveBulkConfirmOpen(false);
    }
  };

  // ── Payroll actions ──────────────────────────────────────────────────────
  const handleDeletePayroll = async () => {
    if (!payrollDeleteTarget) return;
    try {
      await deletePayroll.mutateAsync(payrollDeleteTarget.id);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setPayrollDeleteTarget(null);
    }
  };

  const handleBulkDeletePayroll = async () => {
    try {
      await bulkDeletePayroll.mutateAsync(selectedPayrollIds);
      setSelectedPayrollIds([]);
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setPayrollBulkConfirmOpen(false);
    }
  };

  const handleMarkPaid = (record: any, onSuccess?: () => void) =>
    updatePayroll.mutate({ id: record.id, data: { status: 'PAID' } }, { onSuccess });

  const openEditPayroll = (record: any) => navigate(`/hr/payroll/${record.id}/edit`);

  const handleDownloadPayslip = (record: any) => {
    const rawName = record?.employee ? employeeDisplayName(record.employee) : 'employee';
    // Never let a UUID that ended up in the name field leak into the file name.
    const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      rawName.trim(),
    );
    const fileBase = looksLikeUuid ? record?.employee?.employeeCode || 'employee' : rawName;
    const safeName =
      fileBase
        .trim()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() || 'employee';
    downloadPayslip.mutate({
      id: record.id,
      fileName: `payslip-${safeName}-${record.payPeriod || 'period'}.pdf`,
    });
  };

  const handlePrintPayslip = (record: any) => printPayslip.mutate(record.id);

  const handleBulkStatusChange = async () => {
    if (!bulkPayrollStatus || selectedPayrollIds.length === 0) return;
    try {
      await bulkUpdatePayrollStatus.mutateAsync({
        ids: selectedPayrollIds,
        status: bulkPayrollStatus,
      });
      setSelectedPayrollIds([]);
      setBulkPayrollStatus('');
    } catch {
      // Error toast is handled by the global MutationCache.
    }
  };

  const tabs = [
    { key: 'attendance' as Tab, label: 'Attendance', icon: Clock, count: attendanceMeta?.total ?? attendance.length },
    { key: 'leave' as Tab, label: 'Leave', icon: Calendar, count: leaveMeta?.total ?? leave.length },
    { key: 'payroll' as Tab, label: 'Payroll', icon: DollarSign, count: payrollMeta?.total ?? payroll.length },
  ];

  const newLabel =
    activeTab === 'attendance' ? 'Attendance' : activeTab === 'leave' ? 'Leave' : 'Payroll';

  // Leave and payroll use dedicated pages; attendance stays in a modal.
  const handleNew = () => {
    if (activeTab === 'attendance') setModal('attendance');
    else navigate(activeTab === 'leave' ? '/hr/leave/new' : '/hr/payroll/new');
  };

  const tableWrap = 'bg-surface rounded-xl border border-border overflow-hidden';
  const actionCellClass = 'px-3 py-3';
  const checkboxClass =
    'w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">HR Mgmt.</h1>
            <p className="text-sm text-text-muted">Attendance, leave and payroll for your workforce</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'payroll' && (
            <>
              <button
                onClick={() => setBankSheetOpen(true)}
                className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Landmark size={16} />
                Bank Sheet
              </button>
              <button
                onClick={() => setGeneratePayrollOpen(true)}
                className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
              >
                <CalendarRange size={16} />
                Generate Payroll
              </button>
            </>
          )}
          <button
            onClick={handleNew}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            New {newLabel}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-surface border border-border rounded-lg overflow-hidden w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text hover:bg-background'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-white/20' : 'bg-background text-text-muted'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {attendanceError && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load HR data</span>
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <>
          <FilterBar
            search={attendanceSearch}
            onSearch={setAttendanceSearch}
            placeholder="Search by employee name or code..."
            hasActiveFilters={Boolean(attendanceSearch || attendanceStatus)}
            onClear={() => {
              setAttendanceSearch('');
              setAttendanceStatus('');
            }}
          >
            <select value={attendanceStatus} onChange={(e) => setAttendanceStatus(e.target.value)} className={selectClass}>
              <option value="">All Status</option>
              {ATTENDANCE_FILTER_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </FilterBar>

          {selectedAttendanceIds.length > 0 && (
            <BulkBar
              count={selectedAttendanceIds.length}
              deleting={bulkDeleteAttendance.isPending}
              onDelete={() => setAttendanceBulkConfirmOpen(true)}
              onClear={() => setSelectedAttendanceIds([])}
            />
          )}

          {attendanceLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className={tableWrap}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-background/50">
                      <th className="px-3 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={attendanceSelection.allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = attendanceSelection.someSelected && !attendanceSelection.allSelected;
                          }}
                          onChange={attendanceSelection.toggleAll}
                          disabled={attendance.length === 0}
                          className={checkboxClass}
                          title="Select all"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Check In</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Check Out</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Shift</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-text-muted uppercase w-[110px]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {attendance.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                          {attendanceSearch || attendanceStatus ? (
                            <span>
                              No attendance records match your filters.{' '}
                              <button
                                onClick={() => {
                                  setAttendanceSearch('');
                                  setAttendanceStatus('');
                                }}
                                className="text-primary hover:underline"
                              >
                                Clear filters
                              </button>
                            </span>
                          ) : (
                            'No attendance records yet'
                          )}
                        </td>
                      </tr>
                    ) : (
                      attendance.map((record: any) => (
                        <tr key={record.id} className="hover:bg-background/50 transition-colors">
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={selectedAttendanceIds.includes(record.id)}
                              onChange={() => attendanceSelection.toggleOne(record.id)}
                              className={checkboxClass}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <EmployeeCell
                              employee={record.employee}
                              employeeId={record.employeeId}
                              onNameClick={() => {
                                setViewingEmployeeId(employeeIdOf(record));
                                setViewingEmployeePayroll(null);
                              }}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-text">{formatDateLabel(record.date)}</td>
                          <td className="px-4 py-3">
                            <StatusPill map={ATTENDANCE_STATUS} value={record.status} />
                          </td>
                          <td className="px-4 py-3 text-sm text-text">{formatTime(record.checkIn)}</td>
                          <td className="px-4 py-3 text-sm text-text">{formatTime(record.checkOut)}</td>
                          <td className="px-4 py-3 text-sm text-text">
                            {record.shift?.name || record.shiftType || '—'}
                          </td>
                          <td className={actionCellClass}>
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => setEditingAttendance(record)}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                                title="Edit attendance"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={() => setAttendanceDeleteTarget(record)}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={attendancePage}
                totalPages={attendanceMeta?.totalPages || 1}
                total={attendanceMeta?.total || 0}
                limit={PAGE_LIMIT}
                onPageChange={setAttendancePage}
                itemName="attendance records"
              />
            </div>
          )}
        </>
      )}

      {/* Leave Tab */}
      {activeTab === 'leave' && (
        <>
          <FilterBar
            search={leaveSearch}
            onSearch={setLeaveSearch}
            placeholder="Search by employee name or code..."
            hasActiveFilters={Boolean(leaveSearch || leaveStatus)}
            onClear={() => {
              setLeaveSearch('');
              setLeaveStatus('');
            }}
          >
            <select value={leaveStatus} onChange={(e) => setLeaveStatus(e.target.value)} className={selectClass}>
              <option value="">All Status</option>
              {LEAVE_FILTER_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </FilterBar>

          {selectedLeaveIds.length > 0 && (
            <BulkBar
              count={selectedLeaveIds.length}
              deleting={bulkDeleteLeave.isPending}
              onDelete={() => setLeaveBulkConfirmOpen(true)}
              onClear={() => setSelectedLeaveIds([])}
            />
          )}

          {leaveLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className={tableWrap}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-background/50">
                      <th className="px-3 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={leaveSelection.allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = leaveSelection.someSelected && !leaveSelection.allSelected;
                          }}
                          onChange={leaveSelection.toggleAll}
                          disabled={leave.length === 0}
                          className={checkboxClass}
                          title="Select all"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Period</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Days</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Approve</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-text-muted uppercase w-[110px]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {leave.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                          {leaveSearch || leaveStatus ? (
                            <span>
                              No leave records match your filters.{' '}
                              <button
                                onClick={() => {
                                  setLeaveSearch('');
                                  setLeaveStatus('');
                                }}
                                className="text-primary hover:underline"
                              >
                                Clear filters
                              </button>
                            </span>
                          ) : (
                            'No leave records yet'
                          )}
                        </td>
                      </tr>
                    ) : (
                      leave.map((record: any) => {
                        const rowPending =
                          approveLeave.isPending && approveLeave.variables?.id === record.id;
                        return (
                          <tr key={record.id} className="hover:bg-background/50 transition-colors">
                            <td className="px-3 py-3">
                              <input
                                type="checkbox"
                                checked={selectedLeaveIds.includes(record.id)}
                                onChange={() => leaveSelection.toggleOne(record.id)}
                                className={checkboxClass}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <EmployeeCell
                                employee={record.employee}
                                employeeId={record.employeeId}
                                onNameClick={() => {
                                  setViewingEmployeeId(employeeIdOf(record));
                                  setViewingEmployeePayroll(null);
                                }}
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-text capitalize">
                              {String(record.type || '')
                                .toLowerCase()
                                .replace(/_/g, ' ')}
                            </td>
                            <td className="px-4 py-3 text-sm text-text">
                              {formatDateLabel(record.startDate)} → {formatDateLabel(record.endDate)}
                            </td>
                            <td className="px-4 py-3 text-sm text-text">{record.days ?? '—'}</td>
                            <td className="px-4 py-3">
                              <StatusPill map={LEAVE_STATUS} value={record.status} />
                            </td>
                            <td className="px-4 py-3 text-right">
                              {record.status === 'PENDING' ? (
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() =>
                                      approveLeave.mutate({ id: record.id, data: { status: 'APPROVED' } })
                                    }
                                    disabled={rowPending}
                                    className="px-3 py-1 bg-success/10 text-success rounded-lg text-xs font-medium hover:bg-success/20 transition-colors disabled:opacity-50"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() =>
                                      approveLeave.mutate({ id: record.id, data: { status: 'REJECTED' } })
                                    }
                                    disabled={rowPending}
                                    className="px-3 py-1 bg-danger/10 text-danger rounded-lg text-xs font-medium hover:bg-danger/20 transition-colors disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-text-muted">—</span>
                              )}
                            </td>
                            <td className={actionCellClass}>
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setEditingLeave(record)}
                                  className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                                  title="Edit leave"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  onClick={() => setLeaveDeleteTarget(record)}
                                  className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={leavePage}
                totalPages={leaveMeta?.totalPages || 1}
                total={leaveMeta?.total || 0}
                limit={PAGE_LIMIT}
                onPageChange={setLeavePage}
                itemName="leave records"
              />
            </div>
          )}
        </>
      )}

      {/* Payroll Tab */}
      {activeTab === 'payroll' && (
        <>
          <FilterBar
            search={payrollSearch}
            onSearch={setPayrollSearch}
            placeholder="Search by employee or period..."
            hasActiveFilters={Boolean(payrollSearch || payrollStatus || payrollPeriod)}
            onClear={() => {
              setPayrollSearch('');
              setPayrollStatus('');
              setPayrollPeriod('');
            }}
          >
            <select value={payrollStatus} onChange={(e) => setPayrollStatus(e.target.value)} className={selectClass}>
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="PROCESSED">Processed</option>
              <option value="FINALIZED">Finalized</option>
              <option value="PAID">Paid</option>
            </select>
            <MonthPicker
              value={payrollPeriod}
              onChange={setPayrollPeriod}
              className={selectClass}
              title="Filter by pay period"
              placeholder="All periods"
            />
          </FilterBar>

          {selectedPayrollIds.length > 0 && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-text">
                <span className="font-medium">{selectedPayrollIds.length}</span> selected
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={bulkPayrollStatus}
                  onChange={(e) => setBulkPayrollStatus(e.target.value)}
                  disabled={bulkUpdatePayrollStatus.isPending}
                  className={selectClass}
                  title="Set status for selected records"
                >
                  <option value="">Change status…</option>
                  <option value="DRAFT">Draft</option>
                  <option value="PROCESSED">Processed</option>
                  <option value="FINALIZED">Finalized</option>
                  <option value="PAID">Paid</option>
                </select>
                <button
                  type="button"
                  onClick={handleBulkStatusChange}
                  disabled={!bulkPayrollStatus || bulkUpdatePayrollStatus.isPending}
                  className="px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                >
                  {bulkUpdatePayrollStatus.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <CheckCircle size={14} />
                  )}
                  Apply
                </button>
                <button
                  type="button"
                  onClick={() => setPayrollBulkConfirmOpen(true)}
                  disabled={bulkDeletePayroll.isPending}
                  className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
                >
                  {bulkDeletePayroll.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedPayrollIds([])}
                  disabled={bulkDeletePayroll.isPending || bulkUpdatePayrollStatus.isPending}
                  className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  <X size={14} />
                  Clear selection
                </button>
              </div>
            </div>
          )}

          {payrollLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className={tableWrap}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-background/50">
                      <th className="px-3 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={payrollSelection.allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = payrollSelection.someSelected && !payrollSelection.allSelected;
                          }}
                          onChange={payrollSelection.toggleAll}
                          disabled={payroll.length === 0}
                          className={checkboxClass}
                          title="Select all"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Pay Period</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Gross</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Allowances</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Deductions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Net Pay</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-text-muted uppercase w-[160px]">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payroll.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-12 text-center text-text-muted">
                          {payrollSearch || payrollStatus || payrollPeriod ? (
                            <span>
                              No payroll records match your filters.{' '}
                              <button
                                onClick={() => {
                                  setPayrollSearch('');
                                  setPayrollStatus('');
                                  setPayrollPeriod('');
                                }}
                                className="text-primary hover:underline"
                              >
                                Clear filters
                              </button>
                            </span>
                          ) : (
                            'No payroll records yet'
                          )}
                        </td>
                      </tr>
                    ) : (
                      payroll.map((record: any) => {
                        const rowPending = updatePayroll.isPending && updatePayroll.variables?.id === record.id;
                        return (
                          <tr key={record.id} className="hover:bg-background/50 transition-colors">
                            <td className="px-3 py-3">
                              <input
                                type="checkbox"
                                checked={selectedPayrollIds.includes(record.id)}
                                onChange={() => payrollSelection.toggleOne(record.id)}
                                className={checkboxClass}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <EmployeeCell
                                employee={record.employee}
                                employeeId={record.employeeId}
                                onNameClick={() => {
                                  setViewingEmployeeId(employeeIdOf(record));
                                  setViewingEmployeePayroll(record);
                                }}
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-text">
                              {record.payPeriod}
                              {record.periodStart && record.periodEnd && (
                                <span className="block text-xs text-text-muted">
                                  {formatDateLabel(record.periodStart)} → {formatDateLabel(record.periodEnd)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-text">{currency(record.basicSalary)}</td>
                            <td className="px-4 py-3 text-sm text-text">{currency(record.allowances)}</td>
                            <td className="px-4 py-3 text-sm text-text">{currency(record.deductions)}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-success">
                              {currency(record.netSalary ?? record.netPay)}
                            </td>
                            <td className="px-4 py-3">
                              <StatusPill map={PAYROLL_STATUS} value={record.status} />
                            </td>
                            <td className={actionCellClass}>
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setViewingPayroll(record)}
                                  className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                                  title="View details"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  onClick={() => openEditPayroll(record)}
                                  className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                                  title="Edit payroll"
                                >
                                  <Pencil size={16} />
                                </button>
                                {record.status !== 'PAID' && (
                                  <button
                                    onClick={() => handleMarkPaid(record)}
                                    disabled={rowPending}
                                    className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                                    title="Mark as paid"
                                  >
                                    {rowPending ? (
                                      <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                      <Banknote size={16} />
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => setPayrollDeleteTarget(record)}
                                  className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={payrollPage}
                totalPages={payrollMeta?.totalPages || 1}
                total={payrollMeta?.total || 0}
                limit={PAGE_LIMIT}
                onPageChange={setPayrollPage}
                itemName="payroll records"
              />
            </div>
          )}
        </>
      )}

      {/* Attendance edit modal */}
      <AttendanceEditModal
        open={!!editingAttendance}
        record={editingAttendance}
        onClose={() => setEditingAttendance(null)}
      />

      {/* Leave edit modal */}
      <LeaveEditModal open={!!editingLeave} record={editingLeave} onClose={() => setEditingLeave(null)} />

      {/* Employee details modal (shared by all tabs) */}
      <EmployeeDetailsModal
        open={!!viewingEmployeeId}
        employeeId={viewingEmployeeId}
        onClose={() => {
          setViewingEmployeeId(null);
          setViewingEmployeePayroll(null);
        }}
        payroll={viewingEmployeePayroll}
        onMarkPaid={(record) =>
          handleMarkPaid(record, () =>
            setViewingEmployeePayroll((prev: any) => (prev ? { ...prev, status: 'PAID' } : prev)),
          )
        }
        markPaidPending={updatePayroll.isPending}
        onDownloadPayslip={handleDownloadPayslip}
        onPrintPayslip={handlePrintPayslip}
        payslipPending={downloadPayslip.isPending}
        printPending={printPayslip.isPending}
        onViewFull={(id) => {
          setViewingEmployeeId(null);
          setViewingEmployeePayroll(null);
          navigate(`/employees/${id}`);
        }}
        onEdit={(id) => {
          setViewingEmployeeId(null);
          setViewingEmployeePayroll(null);
          navigate(`/employees/${id}/edit`);
        }}
      />

      {/* Payroll Details Modal */}
      {viewingPayroll && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setViewingPayroll(null)}
        >
          <div
            className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <DollarSign size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-text">Payroll Details</h2>
                  <p className="text-xs text-text-muted">{viewingPayroll.payPeriod}</p>
                </div>
              </div>
              <button
                onClick={() => setViewingPayroll(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <EmployeeCell employee={viewingPayroll.employee} employeeId={viewingPayroll.employeeId} />
                <StatusPill map={PAYROLL_STATUS} value={viewingPayroll.status} />
              </div>

              {viewingPayroll.periodStart && viewingPayroll.periodEnd && (
                <p className="text-sm text-text-muted">
                  Pay period: {formatDateLabel(viewingPayroll.periodStart)} → {formatDateLabel(viewingPayroll.periodEnd)}
                </p>
              )}

              {/* Earnings */}
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-background/50 text-xs font-medium text-text-muted uppercase">Earnings</div>
                <div className="divide-y divide-border">
                  <div className="flex items-center justify-between px-4 py-2 text-sm">
                    <span className="text-text-muted">Basic Salary</span>
                    <span className="text-text font-medium">{currency(viewingPayroll.basicSalary)}</span>
                  </div>
                  {(viewingPayroll.allowanceItems || []).map((item: any, idx: number) => (
                    <div key={`allow-${idx}`} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-text-muted">{item.label || 'Allowance'}</span>
                      <span className="text-text">{currency(item.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-4 py-2 text-sm bg-background/30">
                    <span className="text-text font-medium">Gross Salary</span>
                    <span className="text-text font-semibold">{currency(viewingPayroll.grossSalary)}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-background/50 text-xs font-medium text-text-muted uppercase">Deductions</div>
                <div className="divide-y divide-border">
                  {(viewingPayroll.deductionItems || []).map((item: any, idx: number) => (
                    <div key={`deduct-${idx}`} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-text-muted">{item.reason || 'Deduction'}</span>
                      <span className="text-text">{currency(item.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-4 py-2 text-sm">
                    <span className="text-text-muted">Total Deductions</span>
                    <span className="text-text">{currency(viewingPayroll.deductions)}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2 text-sm">
                    <span className="text-text-muted">Tax</span>
                    <span className="text-text">{currency(viewingPayroll.taxDeduction)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between bg-success/10 border border-success/20 rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-text">Net Pay</span>
                <span className="text-lg font-bold text-success">
                  {currency(viewingPayroll.netSalary ?? viewingPayroll.netPay)}
                </span>
              </div>

              {viewingPayroll.paymentReference && (
                <p className="text-xs text-text-muted">
                  Payment reference: <span className="font-mono">{viewingPayroll.paymentReference}</span>
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => setViewingPayroll(null)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => handlePrintPayslip(viewingPayroll)}
                disabled={printPayslip.isPending}
                className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {printPayslip.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Printer size={16} />
                )}
                Print Payslip
              </button>
              <button
                type="button"
                onClick={() => handleDownloadPayslip(viewingPayroll)}
                disabled={downloadPayslip.isPending}
                className="px-4 py-2 border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {downloadPayslip.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                Download Payslip
              </button>
              {viewingPayroll.status !== 'PAID' && (
                <button
                  type="button"
                  onClick={() => {
                    const record = viewingPayroll;
                    handleMarkPaid(record, () => setViewingPayroll({ ...record, status: 'PAID' }));
                  }}
                  disabled={updatePayroll.isPending}
                  className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/90 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {updatePayroll.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Banknote size={16} />
                  )}
                  Make Paid
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  const record = viewingPayroll;
                  setViewingPayroll(null);
                  if (record) openEditPayroll(record);
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Pencil size={16} />
                Edit Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single delete confirmations */}
      <ConfirmDialog
        open={!!attendanceDeleteTarget}
        title="Delete attendance record"
        description="Are you sure you want to delete this attendance record? This action cannot be undone."
        loading={deleteAttendance.isPending}
        onConfirm={handleDeleteAttendance}
        onCancel={() => setAttendanceDeleteTarget(null)}
      />
      <ConfirmDialog
        open={!!leaveDeleteTarget}
        title="Delete leave record"
        description="Are you sure you want to delete this leave record? This action cannot be undone."
        loading={deleteLeave.isPending}
        onConfirm={handleDeleteLeave}
        onCancel={() => setLeaveDeleteTarget(null)}
      />
      <ConfirmDialog
        open={!!payrollDeleteTarget}
        title="Delete payroll record"
        description={
          <>
            Are you sure you want to delete the payroll record
            {payrollDeleteTarget?.payPeriod ? ` for ${payrollDeleteTarget.payPeriod}` : ''}? This action cannot be undone.
          </>
        }
        loading={deletePayroll.isPending}
        onConfirm={handleDeletePayroll}
        onCancel={() => setPayrollDeleteTarget(null)}
      />

      {/* Bulk delete confirmations */}
      <ConfirmDialog
        open={attendanceBulkConfirmOpen}
        title="Delete attendance records"
        description={`Are you sure you want to delete ${selectedAttendanceIds.length} attendance record${
          selectedAttendanceIds.length === 1 ? '' : 's'
        }? This action cannot be undone.`}
        loading={bulkDeleteAttendance.isPending}
        onConfirm={handleBulkDeleteAttendance}
        onCancel={() => setAttendanceBulkConfirmOpen(false)}
      />
      <ConfirmDialog
        open={leaveBulkConfirmOpen}
        title="Delete leave records"
        description={`Are you sure you want to delete ${selectedLeaveIds.length} leave record${
          selectedLeaveIds.length === 1 ? '' : 's'
        }? This action cannot be undone.`}
        loading={bulkDeleteLeave.isPending}
        onConfirm={handleBulkDeleteLeave}
        onCancel={() => setLeaveBulkConfirmOpen(false)}
      />
      <ConfirmDialog
        open={payrollBulkConfirmOpen}
        title="Delete payroll records"
        description={`Are you sure you want to delete ${selectedPayrollIds.length} payroll record${
          selectedPayrollIds.length === 1 ? '' : 's'
        }? This action cannot be undone.`}
        loading={bulkDeletePayroll.isPending}
        onConfirm={handleBulkDeletePayroll}
        onCancel={() => setPayrollBulkConfirmOpen(false)}
      />

      <AttendanceModal open={modal === 'attendance'} onClose={() => setModal(null)} />

      {generatePayrollOpen && <GeneratePayrollModal onClose={() => setGeneratePayrollOpen(false)} />}

      {bankSheetOpen && <BankSalarySheetModal onClose={() => setBankSheetOpen(false)} />}
    </div>
  );
}
